import React, { useState } from "react";
import { useTranslation } from "react-i18next";

interface Props {
  open: boolean;
  onClose: () => void;
  /** Runs the add-mandate call. Should throw on failure so we can show it. */
  onAuthorize: () => Promise<void>;
}

/**
 * Confirms the ACH mandate disclosure before authorizing an EXISTING bank
 * account that lacks a mandate. Reuses the same disclosure copy as the
 * add-bank flow — the only difference is there's no Plaid step; the bank is
 * already linked, we just capture acceptance and add the mandate on the
 * server.
 */
export const AuthorizeMandateModal: React.FC<Props> = ({
  open,
  onClose,
  onAuthorize,
}) => {
  const { t } = useTranslation();
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  if (!open) return null;

  const handleAuthorize = async () => {
    setSubmitting(true);
    setError(null);
    try {
      await onAuthorize();
      onClose();
    } catch (err: any) {
      console.error(err);
      setError(
        err?.response?.data?.message ||
          t(
            "paymentMethods.errorAddMandate",
            "We couldn't authorize this bank account. Please try again.",
          ),
      );
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div
      role="dialog"
      aria-modal="true"
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4"
    >
      <div className="w-full max-w-lg rounded-xl bg-white shadow-lg">
        <div className="flex items-center justify-between border-b border-gray-100 px-6 py-4">
          <h5 className="text-base font-semibold">
            {t("paymentMethodRow.authorizeAch", "Authorize ACH")}
          </h5>
          <button
            className="text-2xl leading-none text-gray-400 hover:text-gray-600 disabled:cursor-not-allowed"
            onClick={onClose}
            disabled={submitting}
            aria-label="Close"
          >
            &times;
          </button>
        </div>

        <div className="p-6">
          <p className="mb-4 text-sm text-gray-700">
            {t("addBankModal.mandate.intro")}
          </p>
          <div className="mb-4 rounded-lg bg-gray-50 p-4 text-xs leading-relaxed text-muted">
            {t("addBankModal.mandate.disclosure")}
          </div>
          {error && (
            <div className="mb-4 rounded-lg bg-red-50 px-4 py-3 text-sm text-danger">
              {error}
            </div>
          )}
          <div className="flex justify-end gap-2">
            <button
              className="cursor-pointer rounded-lg border border-gray-300 px-4 py-2 text-sm font-medium text-gray-700 transition-colors hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-50"
              onClick={onClose}
              disabled={submitting}
            >
              {t("addBankModal.mandate.cancel")}
            </button>
            <button
              type="button"
              className="cursor-pointer rounded-lg bg-primary px-5 py-2 text-sm font-medium text-white transition-colors hover:bg-primary-light disabled:cursor-not-allowed disabled:opacity-50"
              onClick={handleAuthorize}
              disabled={submitting}
            >
              {submitting
                ? t("addBankModal.connecting")
                : t("addBankModal.mandate.authorize")}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AuthorizeMandateModal;
